import {
   ACCOUNT_ROLES,
   DATA_COLLECTOR_ROLES,
   MANAGER_ROLES,
   WORKSPACE_ROLES
} from "./articleRoles";

const COLLECT = "Collect data";
const ANALYZE = "Analyze results";

export const COLLECTION_ARTICLES = [
   {
      id: "assign-data-collection",
      category: COLLECT,
      title: "Assign a survey to data collectors",
      summary: "Set up a collection round with a target, a location and a field team.",
      roles: ACCOUNT_ROLES,
      related: ["project-team", "survey-analytics"],
      body: `
A **collection round** splits a survey's target number of interviews among your data collectors. Each collector then sees their share on **My tasks**.

Before you start, make sure the survey is published and the data collectors are on the project team. See [Add people to a project](#project-team).

## Create a collection round

1. In the project sidebar, choose **Assignments**, then click **Assign survey**.
2. **Create a new data collection round**: enter a **Collection round name**, pick the **Collection Round Dates**, choose the survey under **Select a survey tool**, and pick the **Data collection type**: **Survey Link** or **In person**.
3. Under **How many people would you like to interview?**, enter the total number of interviews, including any margin for non-responses. Click **Continue**.
4. **Assign a location for this collection round**: click the arrow on a saved location group, or click **Add a new location** to create one.
5. **Confirm the team**: tick the data collectors who will collect. You can't pick more collectors than interviews. Optionally add a note for them (up to 100 characters). Click **Continue**.
6. **Assign Tasks**: the interviews are split evenly. Change any collector's number if you need to, as long as the total stays the same. Click **Preview survey allocation**.
7. Check the summary and click **Confirm survey allocation**.

You'll see how many surveys were assigned. Click **See all assigned surveys** to go back to the list.

> **Tip:** Only published surveys can be assigned, and a survey past its end date can't be picked.

## Follow progress

On the **Assignments** page, the tabs **All**, **Assigned**, **Completed** and **Rejected** show each task's status. The **Surveys** tab shows progress per survey, such as "12/40 tasks".

- Rejected tasks show the collector's **Rejection reason**.
- Use **Search...** and **Filters** to narrow the list, and **Export tasks** to download it as Excel.
- To delete a task, click the bin icon on its row.
`
   },
   {
      id: "my-tasks",
      category: COLLECT,
      title: "Complete your assigned tasks",
      summary: "Find the surveys assigned to you, fill them in, or reject a task.",
      roles: DATA_COLLECTOR_ROLES,
      related: ["collect-offline"],
      body: `
When you sign in as a data collector, **My tasks** opens. At the top you see how many of your tasks are assigned, completed and rejected.

## Get the mobile app

On a phone or tablet, tap **Get the app** in the top bar to open the KAPsurvey mobile app in your device's store: the App Store on iPhone and iPad, Google Play on Android. The button doesn't show on a computer.

## Fill in a survey

1. Under **Your tasks**, open the **Assigned** tab.
2. Find the task, then check its **Deadline**, **Location** and any **Note** from your manager.
3. Click the arrow (**Go to survey**) on the task's row.
4. Fill in the survey and submit it. You come back to **My tasks** afterwards.

## Reject a task

If you can't do a task, click the **X** (**Reject task**) on its row, explain why, and click **Reject task**. Your manager sees your reason. A rejected task can't be taken back.

## Find a task

Use the tabs **All**, **Assigned**, **Completed** and **Rejected**, the **Search...** box, or **Filters** for location, survey, collection type, deadline and status.
`
   },
   {
      id: "collect-offline",
      category: COLLECT,
      title: "Collect responses without internet",
      summary: "How survey forms keep working offline, and how drafts work.",
      related: ["my-tasks"],
      body: `
Survey forms keep working when your connection drops, so you can collect in the field.

## How offline collection works

- An **Online** / **Offline** indicator on the form shows your connection. You'll also see "We are offline now." and "We are back online."
- Responses you submit while offline are queued in your browser and **sent automatically** when you're back online.
- A response leaves the queue only after it has been sent successfully, so it's safe to close the browser.

> **Tip:** Open the form and bookmark it while you still have a connection. You can then reopen it in the field.

## Save drafts

Click **Save as a draft** to keep an unfinished response. Open **Saved drafts** to see them, and click **Submit all drafts** when you're ready.

If a submission fails because of a weak connection, save it as a draft and submit it again once the connection is stable.
`
   },
   {
      id: "audiences",
      category: COLLECT,
      title: "Import and manage an audience",
      summary: "Upload your contacts or respondents from Excel and see their summary.",
      roles: ACCOUNT_ROLES,
      related: ["send-campaign"],
      body: `
An **audience** is a list of contacts or respondents imported from Excel. You can send surveys to it through campaigns.

There are two types:

- **People**: contacts such as partners or customers, with email, WhatsApp number and consent.
- **Humanitarian**: household respondents, with gender, age, household and location details.

## Import an audience

1. In the project sidebar, choose **Audience**, then click **Import audience**.
2. Under **Audience type**, choose **People** or **Humanitarian**.
3. Click **Download template (.xlsx)** and fill it in.
4. Drop the file on **Upload audience file**. One .xlsx file, smaller than 20 MB.
5. Enter an **Audience name** and, optionally, a description. Click **Import**.

You'll see "Audience imported successfully". If something in the file is wrong, the message lists what to fix.

> **Tip:** Always start from the downloaded template. It has the columns and values the import expects.

## Work with an audience

Click an audience to open it:

- **Snapshot** summarizes the list, for example consent, response rates and contacts by country or language. Click **Print Summary** to print it.
- **Recipients list** shows every contact. For People lists you can view and edit a contact's details.

From the list of audiences, use the pencil icon to rename an audience, and the **⋮** menu to **Export** it to Excel or **Delete** it.
`
   },
   {
      id: "send-campaign",
      category: COLLECT,
      title: "Send a survey by email, SMS or WhatsApp",
      summary: "Send a published survey to your contacts, now or on a schedule.",
      roles: MANAGER_ROLES,
      related: ["audiences", "add-funds"],
      body: `
**Automated Campaigns** send a survey link to your contacts by email, SMS or WhatsApp. You can send it once or on a schedule.

Email is free. SMS and WhatsApp are charged per recipient from your balance, and the price depends on the country. See [Add funds to your balance](#add-funds).

## Send a survey

1. In the project sidebar, choose **Automated Campaigns**, then click **Send survey**.
2. **Select the survey you want to send.** Only published surveys are listed, customer feedback surveys included.
3. **Choose how to send your survey**: Email, Whatsapp or SMS. For paid channels, also choose the recipients' country.
4. **Add recipient's contacts** in one of three ways:
   - **Select an audience list**.
   - **Upload contact list**: click **Download template**, fill it in and upload it (Excel, up to 1 MB). Write numbers with the country code, without "+" or "00".
   - **Add contacts manually**.
5. Edit the **Message template** if you like. The survey link is added to the end automatically. WhatsApp messages use a fixed, pre-approved text.
6. Under **Schedule send**, choose **Send once** to send now, or **Repeat** to pick dates, a time and how often to send.
7. Check the **Cost Summary**, then click **Review and send**.
8. Review the details and click **Send survey**. You'll see "Your survey is on its way."

Not ready yet? Click **Save as draft** and finish it later.

## Follow a campaign

The **Automated Campaigns** list shows each campaign's channels, how many were sent and delivered, and the response rate. Open a campaign to see its sends, delivery numbers, responses over time, and **Who opened the link**.

To stop a scheduled send, open the campaign and click **Cancel schedule**. Unused funds are returned to your balance.
`
   },
   {
      id: "survey-analytics",
      category: ANALYZE,
      title: "Explore your survey results",
      summary: "Track progress, read the charts, build insights and export your data.",
      roles: WORKSPACE_ROLES,
      related: ["project-dashboard", "assign-data-collection"],
      body: `
In the project sidebar, choose **Survey Analytics** and pick a survey. Its results are split into five tabs.

## Survey Dashboard

The dashboard shows the survey's progress at a glance:

- The **Target**, **Collected**, **Progress** and **Days left** cards, including whether you're **On track** or **Behind**.
- The **Collection timeline**, comparing what you collected with the pace needed to reach the target.
- **Responses by collection method**, for campaigns, assignments and links.
- **Responses by Location** on a map. Use **Toggle Heatmap** to switch views.
- **Results Snapshot**: click **Click here to pin a graph** to pin two questions to the dashboard.

Click **Download** to save the dashboard as a PDF.

## Survey Results

- **Graphs Report** shows one chart per question, with how many people answered it. Use a chart's menu to export it as PNG or Excel.
- **Table view** lists every submission, one column per question. Click a row's preview icon to open the full submission and print it.

## Insights

Click **Click here to add a graph** to build up to 10 charts. Click a bar in one chart to filter all the others by that answer. **Clear all filters** resets them, and **Export to pdf** saves them.

## Highlighted Data Overview

This tab shows the answers you've chosen to highlight. To set highlights, open the survey's menu on the **Surveys** page and choose **Highlights**. Customer feedback surveys don't have this tab.

## Ask AI

Ask a question about the survey's answers in your own words, such as "Summarise the main findings" or "What themes come up in the open-text answers?". The assistant answers from the submissions this survey collected, in the language you write in.

1. Open the **Ask AI** tab and type your question, or click one of the examples.
2. If the survey has more than one language, choose the translation to read under **Answers to analyse** before you send. It's fixed once the chat starts.
3. Press Enter to send. Shift + Enter adds a new line.

An answer usually takes under two minutes. You can keep working elsewhere: you'll see "Your answer is ready" when it's done. Your chats are listed under **Chats**: click **New chat** to start another, or a chat's delete icon to remove it.

## Export your data

1. Open **Survey Results** → **Table view** and click **Export Submissions**.
2. Click **Continue Exporting**, choose Excel, and tick **Include all media files** if you want photos and other files.
3. Click **Export**.

The file is prepared in the background, so you can keep working. When it's ready, download it from the same window.

> **Tip:** **Print** in the top bar prints the whole page, and **Share survey** gives you the survey link.
`
   }
];
